import React, { useState, useEffect } from 'react';
import './CompensationPlan.css';

const CompensationPlan = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data2024, setData2024] = useState(null);
  const [data2025, setData2025] = useState(null);
  
  // Caitlin compensation inputs
  const [caitlinBase, setCaitlinBase] = useState(70000); // Starting at $70k as requested
  const [caitlinTeachingRate, setCaitlinTeachingRate] = useState(50);
  const [caitlinProfitShare, setCaitlinProfitShare] = useState(20);
  const [caitlinTeachingHours, setCaitlinTeachingHours] = useState(0); // Editable for 2026 projection
  
  // Mafalda compensation inputs
  const [mafaldaBase, setMafaldaBase] = useState(0);
  const [mafaldaTeachingRate, setMafaldaTeachingRate] = useState(70);
  const [mafaldaTeachingHours, setMafaldaTeachingHours] = useState(0);
  const [mafaldaProfitShare, setMafaldaProfitShare] = useState(0);
  
  // Market growth assumptions
  const [growthRate2026, setGrowthRate2026] = useState(0); // Percent growth
  const [targetNetProfitMargin, setTargetNetProfitMargin] = useState(28); // Target net profit margin %
  
  // Editable expenses for 2026
  const [adSpend2026, setAdSpend2026] = useState(null); // null means use calculated value
  const [officeHours2026, setOfficeHours2026] = useState(null); // null means use calculated value
  const [mileage2026, setMileage2026] = useState(null); // null means use calculated value
  
  // Helper function to get API base URL
  const getApiBaseUrl = () => {
    if (window.location.hostname === 'mgmt.storytimechess.com' || 
        window.location.hostname === 'admin.storytimechess.com' ||
        window.location.hostname === 'thinking-cup.com' ||
        window.location.hostname === 'www.thinking-cup.com') {
      return 'https://tcinc-dashboard.onrender.com/api';
    }
    return '/api';
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  const formatPercent = (value) => {
    return `${value.toFixed(1)}%`;
  };
  
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  useEffect(() => {
    fetchHistoricalData();
    fetchSavedPlan();
  }, []);
  
  // Fetch saved compensation plan from database
  const fetchSavedPlan = async () => {
    try {
      setIsLoadingPlan(true);
      const token = localStorage.getItem('token');
      const apiBaseUrl = getApiBaseUrl();
      
      const response = await fetch(`${apiBaseUrl}/compensation-plan/2026`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const plan = result.data;
          // Load saved values
          if (plan.caitlin) {
            setCaitlinBase(plan.caitlin.baseSalary || 70000);
            setCaitlinTeachingRate(plan.caitlin.teachingRate || 50);
            setCaitlinTeachingHours(plan.caitlin.teachingHours || 0);
            setCaitlinProfitShare(plan.caitlin.profitShare || 20);
          }
          if (plan.mafalda) {
            setMafaldaBase(plan.mafalda.baseSalary || 0);
            setMafaldaTeachingRate(plan.mafalda.teachingRate || 70);
            setMafaldaTeachingHours(plan.mafalda.teachingHours || 0);
            setMafaldaProfitShare(plan.mafalda.profitShare || 0);
          }
          if (plan.marketGrowthRate !== undefined) {
            setGrowthRate2026(plan.marketGrowthRate || 0);
          }
          if (plan.targetNetProfitMargin !== undefined) {
            setTargetNetProfitMargin(plan.targetNetProfitMargin || 28);
          }
          if (plan.expenses) {
            if (plan.expenses.officeHours !== undefined && plan.expenses.officeHours !== null) {
              setOfficeHours2026(plan.expenses.officeHours);
            }
            if (plan.expenses.mileage !== undefined && plan.expenses.mileage !== null) {
              setMileage2026(plan.expenses.mileage);
            }
            if (plan.expenses.adSpend !== undefined && plan.expenses.adSpend !== null) {
              setAdSpend2026(plan.expenses.adSpend);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching saved compensation plan:', err);
      // Don't show error to user, just use defaults
    } finally {
      setIsLoadingPlan(false);
    }
  };
  
  // Save compensation plan to database
  const savePlan = async (showSuccess = true) => {
    try {
      const token = localStorage.getItem('token');
      const apiBaseUrl = getApiBaseUrl();
      
      const planData = {
        caitlin: {
          baseSalary: caitlinBase === '' ? 0 : caitlinBase,
          teachingRate: caitlinTeachingRate === '' ? 0 : caitlinTeachingRate,
          teachingHours: caitlinTeachingHours === '' ? 0 : caitlinTeachingHours,
          profitShare: caitlinProfitShare === '' ? 0 : caitlinProfitShare
        },
        mafalda: {
          baseSalary: mafaldaBase === '' ? 0 : mafaldaBase,
          teachingRate: mafaldaTeachingRate === '' ? 0 : mafaldaTeachingRate,
          teachingHours: mafaldaTeachingHours === '' ? 0 : mafaldaTeachingHours,
          profitShare: mafaldaProfitShare === '' ? 0 : mafaldaProfitShare
        },
        marketGrowthRate: growthRate2026 === '' ? 0 : growthRate2026,
        targetNetProfitMargin: targetNetProfitMargin === '' ? 28 : targetNetProfitMargin,
        expenses: {
          officeHours: officeHours2026,
          mileage: mileage2026,
          adSpend: adSpend2026
        }
      };
      
      const response = await fetch(`${apiBaseUrl}/compensation-plan/2026`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(planData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save compensation plan');
      }
      
      const result = await response.json();
      if (result.success) {
        setHasUnsavedChanges(false);
        if (showSuccess) {
          // Show success message (you could add a toast notification here)
          console.log('Compensation plan saved successfully');
        }
      }
    } catch (err) {
      console.error('Error saving compensation plan:', err);
      if (showSuccess) {
        alert('Failed to save compensation plan. Please try again.');
      }
    }
  };
  
  // Auto-save when values change (debounced)
  useEffect(() => {
    // Don't auto-save during initial load
    if (isLoadingPlan || !data2024 || !data2025) {
      return;
    }
    
    const timeoutId = setTimeout(() => {
      savePlan(false); // Silent save
    }, 2000); // Debounce: save 2 seconds after last change
    
    return () => clearTimeout(timeoutId);
  }, [
    caitlinBase, caitlinTeachingRate, caitlinTeachingHours, caitlinProfitShare,
    mafaldaBase, mafaldaTeachingRate, mafaldaTeachingHours, mafaldaProfitShare,
    growthRate2026, targetNetProfitMargin,
    officeHours2026, mileage2026, adSpend2026,
    isLoadingPlan, data2024, data2025
  ]);
  
  const fetchHistoricalData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const apiBaseUrl = getApiBaseUrl();
      
      // Fetch 2024 and 2025 data
      const [response2024, response2025] = await Promise.all([
        fetch(`${apiBaseUrl}/profit-share/annual-ca/2024`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${apiBaseUrl}/profit-share/annual-ca/2025`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);
      
      if (!response2024.ok || !response2025.ok) {
        throw new Error('Failed to fetch historical data');
      }
      
      const data2024_result = await response2024.json();
      const data2025_result = await response2025.json();
      
      setData2024(data2024_result.data);
      setData2025(data2025_result.data);
      
    } catch (err) {
      console.error('Error fetching historical data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate 2025 projected (current * 12/monthsIncluded)
  const calculate2025Projected = () => {
    if (!data2025) return null;
    
    // Get actual months included from the data
    // Use 10 months as the base (through October) and project to 12 months
    const monthsIncluded = 10; // Through October
    const multiplier = 12 / monthsIncluded;
    
    return {
      totalRevenue: data2025.combined.totalRevenue * multiplier,
      appointmentRevenue: data2025.combined.appointmentRevenue * multiplier,
      schoolRevenue: data2025.combined.schoolRevenue * multiplier,
      totalTutorPay: data2025.combined.totalTutorPay * multiplier,
      officeHours: data2025.combined.officeHours * multiplier,
      totalMileage: data2025.combined.totalMileage * multiplier,
      totalAdSpend: data2025.combined.totalAdSpend * multiplier,
      grossProfit: data2025.combined.grossProfit * multiplier,
      caitlinSalary: data2025.combined.caitlinSalary * multiplier,
      netProfit: data2025.combined.netProfit * multiplier,
      profitShareAmount: data2025.combined.profitShareAmount * multiplier,
      caitlinTotalPay: data2025.combined.caitlinTotalPay * multiplier
    };
  };
  
  // Calculate YoY growth
  const calculateGrowth = () => {
    if (!data2024 || !data2025) return null;
    
    const projected2025 = calculate2025Projected();
    if (!projected2025) return null;
    
    return {
      revenue: ((projected2025.totalRevenue - data2024.combined.totalRevenue) / data2024.combined.totalRevenue) * 100,
      netProfit: ((projected2025.netProfit - data2024.combined.netProfit) / data2024.combined.netProfit) * 100,
      grossProfit: ((projected2025.grossProfit - data2024.combined.grossProfit) / data2024.combined.grossProfit) * 100
    };
  };
  
  // Calculate 2026 projections
  const calculate2026Projection = () => {
    const projected2025 = calculate2025Projected();
    if (!projected2025) return null;
    
    // Handle empty string values from inputs
    const growth = growthRate2026 === '' ? 0 : growthRate2026;
    const targetMargin = targetNetProfitMargin === '' ? 0 : targetNetProfitMargin;
    const caitlinBaseVal = caitlinBase === '' ? 0 : caitlinBase;
    const caitlinTeachingRateVal = caitlinTeachingRate === '' ? 0 : caitlinTeachingRate;
    const caitlinTeachingHoursVal = caitlinTeachingHours === '' ? 0 : caitlinTeachingHours;
    const caitlinProfitShareVal = caitlinProfitShare === '' ? 0 : caitlinProfitShare;
    const mafaldaBaseVal = mafaldaBase === '' ? 0 : mafaldaBase;
    const mafaldaTeachingRateVal = mafaldaTeachingRate === '' ? 0 : mafaldaTeachingRate;
    const mafaldaTeachingHoursVal = mafaldaTeachingHours === '' ? 0 : mafaldaTeachingHours;
    const mafaldaProfitShareVal = mafaldaProfitShare === '' ? 0 : mafaldaProfitShare;
    
    const growthMultiplier = 1 + (growth / 100);
    
    const revenue2026 = projected2025.totalRevenue * growthMultiplier;
    const appointmentRevenue2026 = projected2025.appointmentRevenue * growthMultiplier;
    const schoolRevenue2026 = projected2025.schoolRevenue * growthMultiplier;
    
    // Calculate Caitlin's compensation
    const caitlinTeachingPay = caitlinTeachingHoursVal * caitlinTeachingRateVal;
    const caitlinBaseSalary = caitlinBaseVal;
    
    // Calculate Mafalda's compensation
    const mafaldaTeachingPay = mafaldaTeachingHoursVal * mafaldaTeachingRateVal;
    const mafaldaBaseSalary = mafaldaBaseVal;
    
    // Estimate other expenses (assume they grow proportionally)
    const otherTutorPay2026 = projected2025.totalTutorPay * growthMultiplier;
    // Use editable values if set, otherwise calculate from 2025 projected * growth
    const officeHours2026Val = officeHours2026 !== null ? officeHours2026 : (projected2025.officeHours * growthMultiplier);
    const mileage2026Val = mileage2026 !== null ? mileage2026 : (projected2025.totalMileage * growthMultiplier);
    const adSpend2026Val = adSpend2026 !== null ? adSpend2026 : (projected2025.totalAdSpend * growthMultiplier);
    
    // Total expenses
    const totalTutorPay2026 = otherTutorPay2026 + caitlinTeachingPay + mafaldaTeachingPay;
    const totalExpenses2026 = totalTutorPay2026 + officeHours2026Val + mileage2026Val + adSpend2026Val;
    
    // Profit calculations
    const grossProfit2026 = revenue2026 - totalExpenses2026;
    const netProfit2026 = grossProfit2026 - caitlinBaseSalary - mafaldaBaseSalary;
    
    // Profit share calculations
    const caitlinProfitShare2026 = (netProfit2026 * caitlinProfitShareVal) / 100;
    const mafaldaProfitShare2026 = (netProfit2026 * mafaldaProfitShareVal) / 100;
    
    // Total compensation
    const caitlinTotal2026 = caitlinBaseSalary + caitlinTeachingPay + caitlinProfitShare2026;
    const mafaldaTotal2026 = mafaldaBaseSalary + mafaldaTeachingPay + mafaldaProfitShare2026;
    
    // Remaining profit after all compensation
    const remainingProfit = netProfit2026 - caitlinProfitShare2026 - mafaldaProfitShare2026;
    
    // Calculate target net profit based on target margin
    const targetNetProfit = (revenue2026 * targetMargin) / 100;
    const actualMargin = (netProfit2026 / revenue2026) * 100;
    const marginDifference = actualMargin - targetMargin;
    const meetsTarget = actualMargin >= targetMargin;
    
    return {
      revenue: revenue2026,
      appointmentRevenue: appointmentRevenue2026,
      schoolRevenue: schoolRevenue2026,
      grossProfit: grossProfit2026,
      netProfit: netProfit2026,
      remainingProfit: remainingProfit,
      targetNetProfit: targetNetProfit,
      targetNetProfitMargin: targetMargin,
      actualMargin: actualMargin,
      marginDifference: marginDifference,
      meetsTarget: meetsTarget,
      caitlin: {
        base: caitlinBaseSalary,
        teaching: caitlinTeachingPay,
        profitShare: caitlinProfitShare2026,
        total: caitlinTotal2026
      },
      mafalda: {
        base: mafaldaBaseSalary,
        teaching: mafaldaTeachingPay,
        profitShare: mafaldaProfitShare2026,
        total: mafaldaTotal2026
      },
      expenses: {
        otherTutorPay: otherTutorPay2026,
        officeHours: officeHours2026Val,
        mileage: mileage2026Val,
        adSpend: adSpend2026Val,
        total: totalExpenses2026,
        // Store calculated values for reference
        calculatedOfficeHours: projected2025.officeHours * growthMultiplier,
        calculatedMileage: projected2025.totalMileage * growthMultiplier,
        calculatedAdSpend: projected2025.totalAdSpend * growthMultiplier
      }
    };
  };
  
  const projected2025 = calculate2025Projected();
  const growth = calculateGrowth();
  const projection2026 = calculate2026Projection();
  
  if (loading) {
    return (
      <div className="compensation-plan-container">
        <div className="loading">Loading historical data...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="compensation-plan-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }
  
  return (
    <div className="compensation-plan-container">
      <div className="compensation-plan-header">
        <div>
          <h1>💰 CA Market Compensation Plan 2026</h1>
          <p>Plan compensation for Caitlin Goldie and Mafalda Pinto Correia</p>
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
          {hasUnsavedChanges && (
            <span style={{fontSize: '14px', color: '#6b7280'}}>Unsaved changes</span>
          )}
          <button
            onClick={() => savePlan(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: hasUnsavedChanges ? '#059669' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = hasUnsavedChanges ? '#047857' : '#2563eb'}
            onMouseOut={(e) => e.target.style.backgroundColor = hasUnsavedChanges ? '#059669' : '#3b82f6'}
          >
            💾 Save Plan
          </button>
        </div>
      </div>
      
      {/* Historical Data Section */}
      <div className="section">
        <h2>Historical Performance</h2>
        <div className="historical-grid">
          <div className="metric-card">
            <h3>2024 Actual</h3>
            <div style={{flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
              <div className="metric-label">Total Revenue</div>
              <div className="metric-value">{formatCurrency(data2024?.combined.totalRevenue)}</div>
              <div className="metric-label" style={{marginTop: '16px'}}>Net Profit</div>
              <div className="metric-value">{formatCurrency(data2024?.combined.netProfit)}</div>
              <div className="metric-label" style={{marginTop: '16px'}}>Caitlin's Profit Share</div>
              <div className="metric-value" style={{color: '#3b82f6'}}>{formatCurrency(data2024?.combined.profitShareAmount)}</div>
              {data2024?.combined.totalRevenue && (
                <>
                  <div className="metric-label" style={{marginTop: '16px'}}>Gross Margin</div>
                  <div className="metric-value" style={{color: '#10b981'}}>
                    {formatPercent((data2024.combined.grossProfit / data2024.combined.totalRevenue) * 100)}
                  </div>
                  <div className="metric-label" style={{marginTop: '16px'}}>Net Profit Margin</div>
                  <div className="metric-value" style={{color: '#059669'}}>
                    {formatPercent((data2024.combined.netProfit / data2024.combined.totalRevenue) * 100)}
                  </div>
                  <div className="metric-label" style={{marginTop: '16px'}}>Net Margin After Profit Share</div>
                  <div className="metric-value" style={{color: '#0891b2'}}>
                    {formatPercent(((data2024.combined.netProfit - data2024.combined.profitShareAmount) / data2024.combined.totalRevenue) * 100)}
                  </div>
                </>
              )}
            </div>
          </div>
          
          <div className="metric-card">
            <h3>2025 Actual (YTD)</h3>
            <div style={{flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
              <div className="metric-label">Total Revenue</div>
              <div className="metric-value">{formatCurrency(data2025?.combined.totalRevenue)}</div>
              <div className="metric-label" style={{marginTop: '16px'}}>Net Profit</div>
              <div className="metric-value">{formatCurrency(data2025?.combined.netProfit)}</div>
              <div className="metric-label" style={{marginTop: '16px'}}>Caitlin's Profit Share</div>
              <div className="metric-value" style={{color: '#3b82f6'}}>{formatCurrency(data2025?.combined.profitShareAmount)}</div>
              {data2025?.combined.totalRevenue && (
                <>
                  <div className="metric-label" style={{marginTop: '16px'}}>Gross Margin</div>
                  <div className="metric-value" style={{color: '#10b981'}}>
                    {formatPercent((data2025.combined.grossProfit / data2025.combined.totalRevenue) * 100)}
                  </div>
                  <div className="metric-label" style={{marginTop: '16px'}}>Net Profit Margin</div>
                  <div className="metric-value" style={{color: '#059669'}}>
                    {formatPercent((data2025.combined.netProfit / data2025.combined.totalRevenue) * 100)}
                  </div>
                  <div className="metric-label" style={{marginTop: '16px'}}>Net Margin After Profit Share</div>
                  <div className="metric-value" style={{color: '#0891b2'}}>
                    {formatPercent(((data2025.combined.netProfit - data2025.combined.profitShareAmount) / data2025.combined.totalRevenue) * 100)}
                  </div>
                </>
              )}
            </div>
            <div className="metric-note">Through 10 months (January - October)</div>
          </div>
          
          <div className="metric-card">
            <h3>2025 Projected</h3>
            <div style={{flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
              <div className="metric-label">Total Revenue</div>
              <div className="metric-value">{formatCurrency(projected2025?.totalRevenue)}</div>
              <div className="metric-label" style={{marginTop: '16px'}}>Net Profit</div>
              <div className="metric-value">{formatCurrency(projected2025?.netProfit)}</div>
              <div className="metric-label" style={{marginTop: '16px'}}>Caitlin's Profit Share</div>
              <div className="metric-value" style={{color: '#3b82f6'}}>{formatCurrency(projected2025?.profitShareAmount)}</div>
              {projected2025?.totalRevenue && (
                <>
                  <div className="metric-label" style={{marginTop: '16px'}}>Gross Margin</div>
                  <div className="metric-value" style={{color: '#10b981'}}>
                    {formatPercent((projected2025.grossProfit / projected2025.totalRevenue) * 100)}
                  </div>
                  <div className="metric-label" style={{marginTop: '16px'}}>Net Profit Margin</div>
                  <div className="metric-value" style={{color: '#059669'}}>
                    {formatPercent((projected2025.netProfit / projected2025.totalRevenue) * 100)}
                  </div>
                  <div className="metric-label" style={{marginTop: '16px'}}>Net Margin After Profit Share</div>
                  <div className="metric-value" style={{color: '#0891b2'}}>
                    {formatPercent(((projected2025.netProfit - projected2025.profitShareAmount) / projected2025.totalRevenue) * 100)}
                  </div>
                </>
              )}
            </div>
            <div className="metric-note">Projected to 12 months (full year)</div>
          </div>
          
          {growth && (
            <div className="metric-card growth-card">
              <h3>YoY Growth (2024 → 2025)</h3>
              <div style={{flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                <div className="metric-label">Revenue Growth</div>
                <div className="metric-value positive">{formatPercent(growth.revenue)}</div>
                <div className="metric-label" style={{marginTop: '16px'}}>Net Profit Growth</div>
                <div className="metric-value positive">{formatPercent(growth.netProfit)}</div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Compensation Inputs Section */}
      <div className="section">
        <h2>2026 Compensation Plan</h2>
        
        <div className="inputs-grid">
          {/* Caitlin Inputs */}
          <div className="employee-card">
            <h3>👤 Caitlin Goldie</h3>
            <div className="input-group">
              <label>Base Salary (annual)</label>
              <input
                type="number"
                value={caitlinBase}
                onChange={(e) => {
                  const val = e.target.value;
                  setCaitlinBase(val === '' ? '' : parseFloat(val) || 0);
                  setHasUnsavedChanges(true);
                }}
                onBlur={(e) => {
                  if (e.target.value === '') setCaitlinBase(0);
                }}
                step="1000"
              />
            </div>
            <div className="input-group">
              <label>Teaching Rate ($/hr)</label>
              <input
                type="number"
                value={caitlinTeachingRate}
                onChange={(e) => {
                  const val = e.target.value;
                  setCaitlinTeachingRate(val === '' ? '' : parseFloat(val) || 0);
                  setHasUnsavedChanges(true);
                }}
                onBlur={(e) => {
                  if (e.target.value === '') setCaitlinTeachingRate(0);
                }}
                step="5"
              />
            </div>
            <div className="input-group">
              <label>Teaching Hours (projected 2026)</label>
              <input
                type="number"
                value={caitlinTeachingHours}
                onChange={(e) => {
                  const val = e.target.value;
                  setCaitlinTeachingHours(val === '' ? '' : parseFloat(val) || 0);
                  setHasUnsavedChanges(true);
                }}
                onBlur={(e) => {
                  if (e.target.value === '') setCaitlinTeachingHours(0);
                }}
                step="1"
              />
            </div>
            <div className="input-group">
              <label>Profit Share %</label>
              <input
                type="number"
                value={caitlinProfitShare}
                onChange={(e) => {
                  const val = e.target.value;
                  setCaitlinProfitShare(val === '' ? '' : parseFloat(val) || 0);
                  setHasUnsavedChanges(true);
                }}
                onBlur={(e) => {
                  if (e.target.value === '') setCaitlinProfitShare(0);
                }}
                step="0.1"
              />
            </div>
          </div>
          
          {/* Mafalda Inputs */}
          <div className="employee-card">
            <h3>👤 Mafalda Pinto Correia</h3>
            <div className="input-group">
              <label>Base Salary (annual)</label>
              <input
                type="number"
                value={mafaldaBase}
                onChange={(e) => {
                  const val = e.target.value;
                  setMafaldaBase(val === '' ? '' : parseFloat(val) || 0);
                  setHasUnsavedChanges(true);
                }}
                onBlur={(e) => {
                  if (e.target.value === '') setMafaldaBase(0);
                }}
                step="1000"
              />
            </div>
            <div className="input-group">
              <label>Teaching Rate ($/hr)</label>
              <input
                type="number"
                value={mafaldaTeachingRate}
                onChange={(e) => {
                  const val = e.target.value;
                  setMafaldaTeachingRate(val === '' ? '' : parseFloat(val) || 0);
                  setHasUnsavedChanges(true);
                }}
                onBlur={(e) => {
                  if (e.target.value === '') setMafaldaTeachingRate(0);
                }}
                step="5"
              />
            </div>
            <div className="input-group">
              <label>Teaching Hours (projected 2026)</label>
              <input
                type="number"
                value={mafaldaTeachingHours}
                onChange={(e) => {
                  const val = e.target.value;
                  setMafaldaTeachingHours(val === '' ? '' : parseFloat(val) || 0);
                  setHasUnsavedChanges(true);
                }}
                onBlur={(e) => {
                  if (e.target.value === '') setMafaldaTeachingHours(0);
                }}
                step="1"
              />
            </div>
            <div className="input-group">
              <label>Profit Share %</label>
              <input
                type="number"
                value={mafaldaProfitShare}
                onChange={(e) => {
                  const val = e.target.value;
                  setMafaldaProfitShare(val === '' ? '' : parseFloat(val) || 0);
                  setHasUnsavedChanges(true);
                }}
                onBlur={(e) => {
                  if (e.target.value === '') setMafaldaProfitShare(0);
                }}
                step="0.1"
              />
            </div>
          </div>
        </div>
        
        {/* Market Growth Input */}
        <div className="growth-input">
          <label>2026 Market Growth Rate (%)</label>
          <input
            type="number"
            value={growthRate2026}
            onChange={(e) => {
              const val = e.target.value;
              setGrowthRate2026(val === '' ? '' : parseFloat(val) || 0);
              setHasUnsavedChanges(true);
            }}
            onBlur={(e) => {
              if (e.target.value === '') setGrowthRate2026(0);
            }}
            step="0.1"
            placeholder="Enter growth rate (e.g., 12.4 for 12.4%)"
          />
          <div className="input-hint">
            Based on 2024-2025 growth: {growth ? formatPercent(growth.revenue) : 'N/A'}
          </div>
        </div>
        
        {/* Target Net Profit Margin Input */}
        <div className="growth-input">
          <label>
            Target Net Profit Margin (%)
            <span 
              className="tooltip-icon" 
              title="This is the profit margin goal for 2026 after all compensation and expenses. Adjust this to test how management pay and tutor costs affect net profitability."
              style={{
                marginLeft: '8px',
                cursor: 'help',
                fontSize: '14px',
                color: '#6b7280'
              }}
            >
              ℹ️
            </span>
          </label>
          <input
            type="number"
            value={targetNetProfitMargin}
            onChange={(e) => {
              const val = e.target.value;
              setTargetNetProfitMargin(val === '' ? '' : parseFloat(val) || 0);
              setHasUnsavedChanges(true);
            }}
            onBlur={(e) => {
              if (e.target.value === '') setTargetNetProfitMargin(0);
            }}
            step="0.1"
            placeholder="Enter target margin (e.g., 28 for 28%)"
          />
          <div className="input-hint">
            Target profit margin for 2026 after all compensation and expenses
          </div>
        </div>
      </div>
      
      {/* 2026 Projections Section */}
      {projection2026 && (
        <div className="section">
          <h2>2026 Projections</h2>
          
          <div className="projections-grid">
            {/* Market Summary */}
            <div className="projection-card">
              <h3>Market Performance</h3>
              <div className="projection-row">
                <span>Total Revenue:</span>
                <span className="value">{formatCurrency(projection2026.revenue)}</span>
              </div>
              <div className="projection-row">
                <span>Gross Profit:</span>
                <span className="value">{formatCurrency(projection2026.grossProfit)}</span>
              </div>
              <div className="projection-row">
                <span>Gross Margin:</span>
                <span className="value" style={{color: '#10b981'}}>
                  {formatPercent((projection2026.grossProfit / projection2026.revenue) * 100)}
                </span>
              </div>
              <div className="projection-row">
                <span>Actual Net Profit:</span>
                <span className={`value ${projection2026.meetsTarget ? 'positive' : 'negative'}`}>
                  {formatCurrency(projection2026.netProfit)}
                </span>
              </div>
              <div className="projection-row">
                <span>Actual Net Profit Margin:</span>
                <span className={`value ${projection2026.meetsTarget ? 'positive' : 'negative'}`}>
                  {formatPercent(projection2026.actualMargin)}
                </span>
              </div>
              <div className="projection-row">
                <span>Net Margin After Profit Share:</span>
                <span className="value" style={{color: '#0891b2'}}>
                  {formatPercent(((projection2026.netProfit - projection2026.caitlin.profitShare) / projection2026.revenue) * 100)}
                </span>
              </div>
              <div className="projection-row" style={{borderTop: '2px solid #e5e7eb', marginTop: '12px', paddingTop: '12px'}}>
                <span>Target Net Profit:</span>
                <span className="value" style={{color: '#3b82f6', fontWeight: '600'}}>
                  {formatCurrency(projection2026.targetNetProfit)}
                </span>
              </div>
              <div className="projection-row">
                <span>Target Net Profit Margin:</span>
                <span className="value" style={{color: '#3b82f6', fontWeight: '600'}}>
                  {formatPercent(projection2026.targetNetProfitMargin)}
                </span>
              </div>
              <div className="projection-row">
                <span>Margin Difference:</span>
                <span className={`value ${projection2026.marginDifference >= 0 ? 'positive' : 'negative'}`}>
                  {projection2026.marginDifference >= 0 ? '+' : ''}{formatPercent(projection2026.marginDifference)}
                </span>
              </div>
              <div className="projection-row">
                <span>Remaining Profit:</span>
                <span className={`value ${projection2026.remainingProfit < 0 ? 'negative' : 'positive'}`}>
                  {formatCurrency(projection2026.remainingProfit)}
                </span>
              </div>
              {/* Progress indicator */}
              <div style={{marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px', color: '#6b7280'}}>
                  <span>Progress to Target</span>
                  <span>{formatPercent(Math.min(projection2026.actualMargin, projection2026.targetNetProfitMargin))} / {formatPercent(projection2026.targetNetProfitMargin)}</span>
                </div>
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${Math.min((projection2026.actualMargin / projection2026.targetNetProfitMargin) * 100, 100)}%`,
                    height: '100%',
                    backgroundColor: projection2026.meetsTarget ? '#059669' : '#dc2626',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            </div>
            
            {/* Caitlin Compensation */}
            <div className="projection-card">
              <h3>Caitlin Total Compensation</h3>
              <div className="projection-row">
                <span>Base Salary:</span>
                <span className="value">{formatCurrency(projection2026.caitlin.base)}</span>
              </div>
              <div className="projection-row">
                <span>Teaching Pay:</span>
                <span className="value">{formatCurrency(projection2026.caitlin.teaching)}</span>
              </div>
              <div className="projection-row">
                <span>Profit Share:</span>
                <span className="value">{formatCurrency(projection2026.caitlin.profitShare)}</span>
              </div>
              <div className="projection-row total-row">
                <span>Total:</span>
                <span className="value total">{formatCurrency(projection2026.caitlin.total)}</span>
              </div>
            </div>
            
            {/* Mafalda Compensation */}
            <div className="projection-card">
              <h3>Mafalda Total Compensation</h3>
              <div className="projection-row">
                <span>Base Salary:</span>
                <span className="value">{formatCurrency(projection2026.mafalda.base)}</span>
              </div>
              <div className="projection-row">
                <span>Teaching Pay:</span>
                <span className="value">{formatCurrency(projection2026.mafalda.teaching)}</span>
              </div>
              <div className="projection-row">
                <span>Profit Share:</span>
                <span className="value">{formatCurrency(projection2026.mafalda.profitShare)}</span>
              </div>
              <div className="projection-row total-row">
                <span>Total:</span>
                <span className="value total">{formatCurrency(projection2026.mafalda.total)}</span>
              </div>
            </div>
          </div>
          
          {/* Expenses Breakdown */}
          <div className="expenses-card">
            <h3>Other Expenses (2026 Projected)</h3>
            <div className="expenses-grid">
              <div className="expense-item">
                <span>Other Tutor Pay:</span>
                <span>{formatCurrency(projection2026.expenses.otherTutorPay)}</span>
              </div>
              <div className="expense-item editable">
                <label>Office Hours:</label>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <input
                    type="number"
                    value={officeHours2026 !== null ? officeHours2026 : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setOfficeHours2026(val === '' ? null : parseFloat(val) || 0);
                      setHasUnsavedChanges(true);
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '') setOfficeHours2026(null);
                    }}
                    placeholder={formatCurrency(projection2026.expenses.calculatedOfficeHours)}
                    step="0.01"
                    style={{
                      width: '120px',
                      padding: '6px 8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  {officeHours2026 !== null && (
                    <span style={{fontSize: '12px', color: '#6b7280'}}>
                      (was {formatCurrency(projection2026.expenses.calculatedOfficeHours)})
                    </span>
                  )}
                </div>
              </div>
              <div className="expense-item editable">
                <label>Mileage:</label>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <input
                    type="number"
                    value={mileage2026 !== null ? mileage2026 : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setMileage2026(val === '' ? null : parseFloat(val) || 0);
                      setHasUnsavedChanges(true);
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '') setMileage2026(null);
                    }}
                    placeholder={formatCurrency(projection2026.expenses.calculatedMileage)}
                    step="0.01"
                    style={{
                      width: '120px',
                      padding: '6px 8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  {mileage2026 !== null && (
                    <span style={{fontSize: '12px', color: '#6b7280'}}>
                      (was {formatCurrency(projection2026.expenses.calculatedMileage)})
                    </span>
                  )}
                </div>
              </div>
              <div className="expense-item editable">
                <label>Ad Spend:</label>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <input
                    type="number"
                    value={adSpend2026 !== null ? adSpend2026 : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setAdSpend2026(val === '' ? null : parseFloat(val) || 0);
                      setHasUnsavedChanges(true);
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '') setAdSpend2026(null);
                    }}
                    placeholder={formatCurrency(projection2026.expenses.calculatedAdSpend)}
                    step="0.01"
                    style={{
                      width: '120px',
                      padding: '6px 8px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  {adSpend2026 !== null && (
                    <span style={{fontSize: '12px', color: '#6b7280'}}>
                      (was {formatCurrency(projection2026.expenses.calculatedAdSpend)})
                    </span>
                  )}
                </div>
              </div>
              <div className="expense-item total">
                <span>Total Expenses:</span>
                <span>{formatCurrency(projection2026.expenses.total)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompensationPlan;

